import classNames from 'classnames';
import 'mathquill/mathquill.css';
import { createRef, PureComponent } from 'react';

import EquationFieldButtons from './EquationFieldButtons';
import Latex from './Latex';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

export default class EquationField extends PureComponent {
  state = { loadError: null };
  mathquillEl = createRef();

  componentDidUpdate(prevProps) {
    if (!this.props.disabled) {
      if (prevProps.disabled || this.cachedLatexValue !== this.props.value) {
        this._initMathQuillInput();
      }
    }
  }

  componentDidMount() {
    if (!this.props.disabled) {
      this._initMathQuillInput();
    }
  }

  _initMathQuillInput() {
    const { autoFocus, onEnter, value } = this.props;
    this.cachedLatexValue = value;
    import(
      /* webpackChunkName: "mathquill" */ 'imports-loader?imports=default|jquery|$&additionalCode=window.jQuery=$;!exports-loader?exports=default|window.MathQuill!mathquill/mathquill.min.js'
    )
      .then(({ default: MathQuill }) => {
        // may have unmounted / disabled during load
        if (this.mathquillEl.current) {
          this.setState({ loadError: null });
          const MQ = MathQuill.getInterface(2);

          this.mathquill = MQ.MathField(this.mathquillEl.current, {
            handlers: {
              edit: this.mathquillEdit,
              enter: onEnter,
            },
          });
          this.mathquill.latex(value);
          if (autoFocus) {
            setTimeout(this.focus, 0);
          }
        }
      })
      .catch(error => {
        this.setState({ loadError: error });
      });
  }

  retryInit = () => {
    this.setState({ loadError: null }, this._initMathQuillInput);
  };

  mathquillEdit = () => {
    const { onChange, value } = this.props;
    const latex = this.mathquill.latex();
    this.cachedLatexValue = latex;
    onChange && latex !== value && onChange(latex);
  };

  handleMQCommand = val => this.mathquill?.cmd(val);
  handleMQWrite = val => this.mathquill?.write(val);

  focus = () => this.mathquill?.focus();

  render() {
    const { className, disabled, extraFieldButtonsString, onEnter, printMode, value, ...rest } = this.props;
    const { loadError } = this.state;
    return (
      <div
        className={classNames(className, { root_border: disabled, root_printMode: printMode })}
        css={rootCss}
        onClick={this.focus}
        {...rest}
      >
        {disabled ?
          <div className="EF-latexResult">
            <Latex displayMode>{value}</Latex>
          </div>
        : loadError ?
          <Alert
            action={
              <Button color="inherit" onClick={this.retryInit} size="small">
                RETRY
              </Button>
            }
            severity="error"
          >
            Sorry, the equation input failed to load properly
          </Alert>
        : <>
            <span className="EF-mathquill" ref={this.mathquillEl} />
            {!printMode && (
              <EquationFieldButtons
                className="EF-buttons"
                extraFieldButtonsString={extraFieldButtonsString}
                onMQCommand={this.handleMQCommand}
                onMQWrite={this.handleMQWrite}
              />
            )}
          </>
        }
      </div>
    );
  }
}

const rootCss = ({ palette, shape: { borderRadius }, spacing, transitions }) => ({
  // declared before `.EF-mathquill` so its `&:hover` / `&.mq-focused` rules still win on ties, as in the JSS version
  '&.root_printMode .EF-mathquill.mq-math-mode.mq-editable-field': {
    borderWidth: '0 0 1px 0',
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
    borderColor: 'rgba(0, 0, 0, 0.42)',
  },
  '.EF-mathquill': {
    width: '100%',
    minWidth: 100,
    minHeight: 56,
    padding: spacing(2, 1),
    backgroundColor: palette.background.paper,
    borderRadius,
    boxShadow: 'none !important',
    transition: transitions.create('border-color'),
    display: 'block',
    border: `1px solid ${palette.action.disabled}`,

    '&.mq-math-mode.mq-editable-field': {
      border: `1px solid ${palette.form.input}`,
      '&:hover': { borderColor: palette.form.inputHover },
      '&.mq-focused': {
        borderRadius,
        borderWidth: 2,
        paddingTop: parseInt(spacing(2)) - 1,
        paddingBottom: parseInt(spacing(2)) - 1,
        borderColor: palette.primary.main,
        '& + .EF-buttons': { opacity: 1 },
      },
      '& .mq-cursor': {
        borderColor: palette.text.primary,
      },
    },

    // '&:focus': { outline: 'none !important' },
  },
  '.EF-latexResult': {
    padding: spacing(1),
    '& > div > .katex-display': {
      display: 'inline-block',
      margin: 0,
    },
  },
  '.EF-buttons': {
    opacity: 0.5,
    transition: transitions.create('opacity'),
  },
  '&.root_border': {
    border: `1px solid ${palette.divider}`,
    borderRadius,
  },
});
